import React, { useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import capitalize from 'lodash/capitalize';
import PropTypes from 'prop-types';
import { TickIcon } from '@/assets/images';
import stopPropagation from '@/constants/ui/stopPropagation';
import translate from '@/lib/intl/translate';
import { GroupType } from '@/propTypes';

const renderSelectValue = (selectedOptions = []) =>
  selectedOptions
    ?.map(({ groupDisplayName }) => capitalize(groupDisplayName))
    ?.join(', ');

function InviteSelector({
  BottomComponent,
  classes,
  disabled,
  multiple,
  onClose,
  options,
  selectedOptions,
  handleChange,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const openSelector = useCallback(() => setIsOpen(true), []);
  const closeSelector = useCallback(() => {
    if (onClose) {
      onClose(selectedOptions);
    }

    setIsOpen(false);
  }, [selectedOptions]);

  const Options = useMemo(
    () =>
      options?.map((value = {}) => {
        const { groupDisplayName } = value;

        const isSelected = selectedOptions?.find(
          (option) => option.groupDisplayName === groupDisplayName,
        );

        return (
          <MenuItem key={groupDisplayName} value={groupDisplayName}>
            {isSelected ? <TickIcon /> : null}
            <Box ml={isSelected ? 2 : 4}>
              <Typography variant="body1">
                {capitalize(groupDisplayName)}
              </Typography>
            </Box>
          </MenuItem>
        );
      }),
    [options, selectedOptions],
  );

  const onChange = useCallback(
    ({ target: { value } }) => {
      const newOptions = multiple
        ? value?.filter((val) => Boolean(val))
        : [value];

      handleChange(
        newOptions.map((groupDisplayName) =>
          options.find(
            (option) => option.groupDisplayName === groupDisplayName,
          ),
        ),
      );
    },
    [multiple, options],
  );

  const BottomPart = useMemo(() => {
    if (BottomComponent) {
      return (
        <div>
          <Divider />
          <BottomComponent onClose={closeSelector} />
        </div>
      );
    }
    return null;
  }, [BottomComponent, closeSelector]);

  const values = useMemo(() => {
    const groupDisplayNames =
      selectedOptions?.map(({ groupDisplayName }) => groupDisplayName) || [];

    return multiple ? groupDisplayNames : groupDisplayNames[0] || '';
  }, [selectedOptions]);

  return (
    <Select
      classes={classes}
      disabled={disabled}
      disableUnderline
      id="options-selector"
      labelId="options-label"
      label={translate('COMPANY_PAGE_PERMISSION_TYPE_LABEL')}
      multiple={multiple}
      onChange={onChange}
      onClick={stopPropagation}
      onClose={closeSelector}
      onOpen={openSelector}
      open={isOpen}
      renderValue={() => renderSelectValue(selectedOptions)}
      value={values}
      variant="standard"
    >
      {Options}
      {BottomPart}
    </Select>
  );
}

InviteSelector.propTypes = {
  BottomComponent: PropTypes.func,
  classes: PropTypes.object,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  onClose: PropTypes.func,
  options: GroupType,
  selectedOptions: GroupType,
  handleChange: PropTypes.func,
};

InviteSelector.defaultProps = {
  BottomComponent: null,
  classes: null,
  disabled: false,
  handleChange: () => {},
  multiple: false,
  onClose: () => {},
  options: [],
  selectedOptions: [],
};

export default InviteSelector;
